import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { catalogueAPI, Product, Category, API_BASE_URL } from '../../services/api';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  FolderIcon,
  DocumentIcon,
  DocumentTextIcon,
  EyeIcon,
  TableCellsIcon,
  ListBulletIcon,
  BookOpenIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import ProductModal from './ProductModal';
import CategoryModal from './CategoryModal';
import { Card, CardBody, Button, Spinner, Tabs, EmptyState, PageHero } from '../ui';
import ExcelImportModal from '../common/ExcelImportModal';
import { catalogueProductImportSchema, ExcelImportRow } from '../../utils/excelImport';

const CatalogueManagement: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>();
  const [showProductModal, setShowProductModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [activeTab, setActiveTab] = useState<'products' | 'categories'>('products');
  const [viewMode, setViewMode] = useState<'inline' | 'grouped'>('inline');
  const [showImportModal, setShowImportModal] = useState(false);

  useEffect(() => {
    loadData();
  }, [selectedCategory, searchTerm]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [productsData, categoriesData] = await Promise.all([
        catalogueAPI.getProducts({
          categoryId: selectedCategory,
          search: searchTerm || undefined,
          isActive: true,
        }),
        catalogueAPI.getCategories(),
      ]);
      setProducts(productsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading catalogue data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await catalogueAPI.deleteProduct(id);
      loadData();
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Failed to delete product');
    }
  };

  const handleDeleteCategory = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      await catalogueAPI.deleteCategory(id);
      loadData();
    } catch (error: any) {
      console.error('Error deleting category:', error);
      alert(error.response?.data?.message || 'Failed to delete category');
    }
  };

  const handleEditProduct = async (product: Product) => {
    try {
      // Fetch full product details including cost ranges
      const fullProduct = await catalogueAPI.getProduct(product.id);
      setSelectedProduct(fullProduct);
      setShowProductModal(true);
    } catch (error) {
      console.error('Error loading product details:', error);
      alert('Failed to load product details');
    }
  };

  const handleViewProduct = async (product: Product) => {
    navigate(`/admin/catalogue/${product.id}`);
  };

  const handleViewProductDocs = (product: Product) => {
    navigate(`/admin/catalogue/${product.id}/docs`);
  };

  const handleProductModalClose = () => {
    setShowProductModal(false);
    setSelectedProduct(null);
    loadData();
  };

  const handleImportProducts = async (rows: ExcelImportRow[]) => {
    await catalogueAPI.bulkImportProducts(rows);
    await loadData();
  };

  const handleCategoryModalClose = () => {
    setShowCategoryModal(false);
    loadData();
  };

  // Calculate unique quantity ranges across all products
  const quantityRanges = useMemo(() => {
    const ranges = new Set<string>();
    products.forEach((product) => {
      product.costRanges?.forEach((range) => {
        const unit = range.unit || product.unit || 'unit';
        const rangeKey = `${unit}|${range.minQuantity}-${range.maxQuantity || 'unlimited'}`;
        ranges.add(rangeKey);
      });
    });
    return Array.from(ranges).sort((a, b) => {
      const [aUnit, aRange] = a.split('|');
      const [bUnit, bRange] = b.split('|');
      if (aUnit !== bUnit) return aUnit.localeCompare(bUnit);
      const aMin = parseInt(aRange.split('-')[0]);
      const bMin = parseInt(bRange.split('-')[0]);
      return aMin - bMin;
    });
  }, [products]);

  // Get price for a specific product and range
  const getPriceForRange = (product: Product, rangeKey: string) => {
    const [unit, quantityRange] = rangeKey.split('|');
    const [minStr, maxStr] = quantityRange.split('-');
    const min = parseInt(minStr);
    const max = maxStr === 'unlimited' ? null : parseInt(maxStr);

    const range = product.costRanges?.find(
      (r) => (r.unit || product.unit || 'unit') === unit && r.minQuantity === min && (max === null ? r.maxQuantity === null : r.maxQuantity === max)
    );

    return range ? `$${parseFloat(range.pricePerUnit).toFixed(2)}` : '-';
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Enhanced Hero Section */}
      <PageHero
        title="Product Catalogue"
        subtitle="Manage your product catalogue, categories, and pricing"
        icon={<BookOpenIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white drop-shadow-lg" />}
        accentColor="purple"
      >
        {/* Tabs */}
        <div className="mt-4 w-full">
          <Tabs
            tabs={[
              { id: 'products', label: 'Products' },
              { id: 'categories', label: 'Categories' },
            ]}
            activeTab={activeTab}
            onChange={(id) => setActiveTab(id as 'products' | 'categories')}
          />
        </div>
      </PageHero>

      {activeTab === 'products' ? (
        <>
          {/* Filters and Actions */}
          <div className="mt-6 flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-neutral-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-neutral-300 rounded-lg leading-5 bg-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
                />
              </div>
            </div>
            <div className="sm:w-64">
              <select
                value={selectedCategory || ''}
                onChange={(e) => setSelectedCategory(e.target.value ? parseInt(e.target.value) : undefined)}
                className="block w-full pl-3 pr-10 py-2 text-base border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-lg transition-colors"
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              {/* View Mode Toggle */}
              <div className="inline-flex rounded-lg shadow-sm" role="group">
                <button
                  type="button"
                  onClick={() => setViewMode('inline')}
                  className={`inline-flex items-center px-3 py-2 text-sm font-medium border ${viewMode === 'inline'
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50'
                    } rounded-l-lg focus:z-10 focus:ring-2 focus:ring-primary-500 transition-colors`}
                  title="Inline View"
                >
                  <ListBulletIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('grouped')}
                  className={`inline-flex items-center px-3 py-2 text-sm font-medium border-t border-b border-r ${viewMode === 'grouped'
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50'
                    } rounded-r-lg focus:z-10 focus:ring-2 focus:ring-primary-500 transition-colors`}
                  title="Grouped Columns View"
                >
                  <TableCellsIcon className="h-5 w-5" />
                </button>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowImportModal(true)}
                leftIcon={<ArrowUpTrayIcon className="h-5 w-5" />}
              >
                Import Excel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setSelectedProduct(null);
                  setShowProductModal(true);
                }}
                leftIcon={<PlusIcon className="h-5 w-5" />}
              >
                Add Product
              </Button>
            </div>
          </div>

          {/* Products Table */}
          <Card variant="elevated" className="mt-6 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900">
                      Product Name
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900">
                      SKU
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900">
                      Category
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900">
                      Unit
                    </th>
                    {viewMode === 'inline' ? (
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900">
                        Cost Ranges
                      </th>
                    ) : (
                      quantityRanges.map((rangeKey) => {
                        const [unit, quantityRange] = rangeKey.split('|');
                        const [rawMin, rawMax] = quantityRange.split('-');
                        const min = `${unit} ${rawMin}`;
                        const max = rawMax === 'unlimited' ? 'no limit' : rawMax;
                        return (
                          <th
                            key={rangeKey}
                            className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900 whitespace-nowrap"
                          >
                            {min}-{max === 'unlimited' ? '∞' : max}
                          </th>
                        );
                      })
                    )}
                    <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200 bg-white">
                  {loading ? (
                    <tr>
                      <td
                        colSpan={viewMode === 'inline' ? 6 : 5 + quantityRanges.length}
                        className="px-3 py-8 text-center"
                      >
                        <Spinner size="lg" />
                      </td>
                    </tr>
                  ) : products.length === 0 ? (
                    <tr>
                      <td
                        colSpan={viewMode === 'inline' ? 6 : 5 + quantityRanges.length}
                        className="px-3 py-4"
                      >
                        <EmptyState
                          icon={<DocumentIcon className="h-12 w-12" />}
                          title="No products found"
                          description="Get started by adding a new product."
                        />
                      </td>
                    </tr>
                  ) : (
                    products.map((product) => (
                      <tr
                        key={product.id}
                        className="hover:bg-neutral-50 cursor-pointer transition-colors"
                        onClick={() => handleViewProduct(product)}
                      >
                        <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-neutral-900">
                          {product.name}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-neutral-500">
                          {product.sku}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-neutral-500">
                          {product.category?.name || '-'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-neutral-500">
                          {product.unit || '-'}
                        </td>
                        {viewMode === 'inline' ? (
                          <td className="px-3 py-4 text-sm text-neutral-500">
                            {product.costRanges && product.costRanges.length > 0 ? (
                              <div className="space-y-1">
                                {product.costRanges.map((range, idx) => (
                                  <div key={idx} className="text-xs">
                                    {range.unit || product.unit || 'unit'}{' '}
                                    {range.minQuantity}
                                    {range.maxQuantity ? `-${range.maxQuantity}` : '+'}: $
                                    {parseFloat(range.pricePerUnit).toFixed(2)}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-neutral-400">No pricing</span>
                            )}
                          </td>
                        ) : (
                          quantityRanges.map((rangeKey) => (
                            <td
                              key={rangeKey}
                              className="whitespace-nowrap px-3 py-4 text-sm text-neutral-500"
                            >
                              {getPriceForRange(product, rangeKey)}
                            </td>
                          ))
                        )}
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewProduct(product);
                            }}
                            className="text-primary-600 hover:text-primary-900 mr-4 transition-colors"
                            title="Open product overview"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewProductDocs(product);
                            }}
                            className="text-cyan-600 hover:text-cyan-900 mr-4 transition-colors"
                            title="Open PDF documents"
                          >
                            <DocumentTextIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditProduct(product);
                            }}
                            className="text-primary-600 hover:text-primary-900 mr-4 transition-colors"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteProduct(product.id);
                            }}
                            className="text-error-600 hover:text-error-900 transition-colors"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      ) : (
        <>
          {/* Categories Section */}
          <div className="mt-6 flex justify-end">
            <Button
              variant="primary"
              onClick={() => setShowCategoryModal(true)}
              leftIcon={<PlusIcon className="h-5 w-5" />}
            >
              Add Category
            </Button>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {categories.map((category) => (
              <Card key={category.id} variant="elevated" hoverable>
                <CardBody className="px-6 py-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FolderIcon className="h-6 w-6 text-primary-600 mr-3" />
                      <div>
                        <h3 className="text-sm font-medium text-neutral-900">{category.name}</h3>
                        <p className="text-sm text-neutral-500">
                          {category._count?.products || 0} products
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteCategory(category.id)}
                      className="text-error-600 hover:text-error-900 transition-colors"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                  {category.description && (
                    <p className="mt-2 text-sm text-neutral-500">{category.description}</p>
                  )}
                </CardBody>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Modals */}
      {showProductModal && (
        <ProductModal
          product={selectedProduct}
          categories={categories}
          onClose={handleProductModalClose}
          onCategoryCreated={(newCat) => setCategories((prev) => [...prev, newCat])}
        />
      )}

      {showCategoryModal && (
        <CategoryModal onClose={handleCategoryModalClose} />
      )}

      <ExcelImportModal
        isOpen={showImportModal}
        title="Import Catalogue Products"
        schema={catalogueProductImportSchema}
        rowsLabel="products"
        templateFileName="catalogue-products-import-template.csv"
        onClose={() => setShowImportModal(false)}
        onConfirm={handleImportProducts}
      />
    </div>
  );
};

export default CatalogueManagement;
